import { useMemo } from "react"
import { Autocomplete, Box, IconButton, Stack, TextField } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"
import { CONDITION_FIELDS } from "./condition-field-config"
import { ConditionValueInput } from "./condition-value-input"

export function RuleConditionRow({ condition, onChange, onRemove, onAdd, canRemove = true }) {
    const { conditionFields: ctxFields } = useBoundContext()
    const activeFields = ctxFields || CONDITION_FIELDS
    const fieldOptions = useMemo(buildFieldOptions, [activeFields])
    const fieldConfig = activeFields.find(matchesConditionField) || null
    const operatorOptions = fieldConfig ? fieldConfig.operators.map(toOperatorOption) : []

    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
            <Autocomplete
                sx={{ minWidth: 160, flex: 1 }}
                options={fieldOptions}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
                isOptionEqualToValue={(opt, val) => opt.value === (val?.value ?? val)}
                value={fieldOptions.find((o) => o.value === condition.field) || null}
                onChange={handleFieldChange}
                renderInput={(params) => <TextField {...params} placeholder="Select a field..." />}
            />
            <Autocomplete
                sx={{ minWidth: 140, flex: 1 }}
                options={operatorOptions}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
                isOptionEqualToValue={(opt, val) => opt.value === (val?.value ?? val)}
                value={operatorOptions.find((o) => o.value === condition.operator) || null}
                onChange={handleOperatorChange}
                disabled={!condition.field}
                renderInput={(params) => <TextField {...params} placeholder="Select a condition..." />}
            />
            <Box sx={{ flex: 2, minWidth: 180 }}>
                <ConditionValueInput
                    fieldConfig={fieldConfig}
                    value={condition.value}
                    onChange={handleValueChange}
                    operator={condition.operator}
                />
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton
                    onClick={onAdd}
                    sx={{ color: "primary.main", p: 0.5 }}
                    data-cy="add-condition"
                    aria-label="Add condition"
                >
                    <Iconify icon="gg:add" width={24} />
                </IconButton>
                <IconButton
                    onClick={onRemove}
                    disabled={!canRemove}
                    sx={{ color: "text.secondary", p: 0.5 }}
                    data-cy="remove-condition"
                    aria-label="Remove condition"
                >
                    <Iconify icon="mdi:close-circle-outline" width={24} />
                </IconButton>
            </Stack>
        </Stack>
    )

    function handleFieldChange(_, selected) {
        onChange({
            field: selected?.value || "",
            operator: "",
            value: null,
        })
    }

    function handleOperatorChange(_, selected) {
        const newOp = selected?.value || ""
        const valueType = getOperatorValueType(newOp)
        const prevValueType = getOperatorValueType(condition.operator)
        onChange({
            ...condition,
            operator: newOp,
            value: valueType !== prevValueType ? getDefaultValueForOperator(newOp) : condition.value,
        })
    }

    function getOperatorValueType(op) {
        if (!op) return "none"
        if (op === "between") return "range"
        if (
            op === "is any of" ||
            op === "is none of" ||
            op === "has any of" ||
            op === "has none of" ||
            op === "has all of"
        )
            return "multi"
        if (
            op === "is mandatory" ||
            op === "is optional" ||
            op === "is retired" ||
            op === "is not retired" ||
            op === "is true" ||
            op === "is false" ||
            op === "has any"
        )
            return "boolean"
        if (op === "greater than" || op === "less than" || op === "exactly" || op === "equals") return "numeric"
        if (op === "contains" || op === "starts with") return "text"
        if (op === "more often than" || op === "less often than") return "frequency"
        return "single"
    }

    function getDefaultValueForOperator(operator) {
        const valueType = getOperatorValueType(operator)
        if (valueType === "boolean" || valueType === "none") return null
        if (fieldConfig?.valueComponent === "numeric") {
            if (operator === "between") return { min: 0, max: 0 }
            return 0
        }
        if (fieldConfig?.valueComponent === "frequency") return { interval: 1, period: "Month" }
        return null
    }

    function handleValueChange(newValue) {
        onChange({ ...condition, value: newValue })
    }

    function buildFieldOptions() {
        return activeFields.map((f) => ({ value: f.value, label: f.label }))
    }

    function matchesConditionField(f) {
        return f.value === condition.field
    }

    function toOperatorOption(o) {
        return { value: o, label: o }
    }
}
